import { CheckoutIntentKind } from './checkout-intent-kind';
import type { CheckoutQuote } from '@wix/ai-scheduling-assistant-common-lib/rpc';
import { ConfirmResult, ElementsMode, StripeElements, StripeInstance } from './stripe/embedded';

/**
 * The browser half of a purchase made on our own checkout screen.
 *
 * The screen renders the form; this decides what the outcome of pressing Pay actually was. It is a
 * separate module for the same reason lib/upgrade-confirmation is: money verdicts are worth testing
 * without a browser, and the wrong verdict here is not a display bug — it either tells a buyer their
 * payment failed when it went through, or shows them a success they never paid for.
 */

/**
 * What happened when the buyer confirmed.
 *
 * - `paid` — the money is taken, or is settling asynchronously. Either way the grant arrives on the
 *   webhook, so the screen shows success and polls for the plan.
 * - `saved` — nothing was owed today and a card was stored so the subscription can bill when its
 *   free period ends. Also a success, and deliberately NOT folded into `paid`: a buyer told "paid"
 *   for a charge that never happened will look for it on their statement.
 * - `declined` — DEFINITIVE. Stripe left the payment unfinished: a refused card, a failed
 *   authentication, or a buyer who closed the challenge. Nothing was charged. The buyer may fix
 *   their card and try again, which needs a FRESH idempotency key — an exact-key retry would replay
 *   the decline for 24 hours.
 * - `unsettled` — the outcome is UNKNOWN (transport loss, an unexpected shape). The payment may
 *   already have succeeded, so this must never render as "payment failed" and must never mint a new
 *   key: the screen says it is finishing, keeps the key, and retries or polls until it converges.
 */
export type CheckoutVerdict = 'paid' | 'saved' | 'declined' | 'unsettled';

/**
 * The ONE payment status that means the buyer has actually paid.
 *
 * `processing` and `requires_capture` are deliberately not here, and that is a correction: they read
 * like success and are not. `processing` is an asynchronous method that can still fail, and
 * `requires_capture` is money authorised and explicitly not taken. Calling either of them paid
 * closes the checkout, tells the buyer their credits are on the way, and can flip the picker to
 * subscribed — for a purchase where `invoice.paid` may never arrive and no grant ever happens.
 *
 * They fall through to `unsettled` instead, which keeps the idempotency key, cannot charge twice,
 * and leaves the webhook as the entitlement boundary it already is.
 */
const SETTLED_PAYMENT_STATUSES = new Set(['succeeded']);

/**
 * Statuses that are neither paid nor refused — the payment is still moving.
 *
 * These must stay `unsettled` rather than `declined`, because a declined verdict mints a FRESH
 * idempotency key: a buyer told their in-flight payment failed would retry and could be charged a
 * second time for the one that then succeeded.
 */
const IN_FLIGHT_PAYMENT_STATUSES = new Set(['processing', 'requires_capture', 'requires_action']);
/** Setup statuses that mean the card is stored. */
const SETTLED_SETUP_STATUSES = new Set(['succeeded']);

/**
 * Stripe error types that describe the PAYMENT rather than the request.
 *
 * `card_error` is a refusal by the issuer and `validation_error` is a field the buyer can fix — both
 * are definitive and both are the buyer's to act on. Everything else (`api_error`,
 * `api_connection_error`, an unrecognised type) says the CALL failed, which tells us nothing about
 * whether the payment did: those must fall through to `unsettled`.
 */
const DEFINITIVE_ERROR_TYPES = new Set(['card_error', 'validation_error', 'invalid_request_error']);

/**
 * Reduce a Stripe confirm result to a verdict.
 *
 * The order matters and is deliberate: a SUCCEEDED intent wins over an error that arrived beside it.
 * Stripe can return both — an error from a later step of the same call, on an intent that already
 * settled — and reading the error first would tell a buyer their successful payment failed.
 */
export function verdictFromConfirm(
  intentKind: CheckoutIntentKind,
  result: ConfirmResult | undefined,
): CheckoutVerdict {
  if (!result) {
    return 'unsettled';
  }
  const wantsSetup = intentKind === CheckoutIntentKind.CHECKOUT_INTENT_SETUP;
  const settledStatuses = wantsSetup ? SETTLED_SETUP_STATUSES : SETTLED_PAYMENT_STATUSES;
  const success = wantsSetup ? 'saved' : 'paid';

  const status = wantsSetup ? result.setupIntent?.status : result.paymentIntent?.status;
  if (status && settledStatuses.has(status)) {
    return success;
  }
  // An error can carry the intent it failed on, and THAT intent's status is the authoritative record
  // of what happened to the money — it outranks the error beside it, in both directions.
  //
  // Read the intent that MATCHES the confirm: a `confirmSetup` failure carries `setup_intent`, not
  // `payment_intent`. Reading the payment one on a setup path ignores the only authoritative field
  // there is and calls a succeeded setup a decline.
  const errorIntentStatus = wantsSetup
    ? result.error?.setup_intent?.status
    : result.error?.payment_intent?.status;
  if (errorIntentStatus && settledStatuses.has(errorIntentStatus)) {
    return success;
  }
  // Including when it is still moving. An `invalid_request_error` carrying an intent that reads
  // `processing` is not a refusal: classifying it as one mints a fresh idempotency key, and the
  // buyer can then be charged a second time for the payment that was already on its way.
  if (errorIntentStatus && IN_FLIGHT_PAYMENT_STATUSES.has(errorIntentStatus)) {
    return 'unsettled';
  }
  if (result.error) {
    return DEFINITIVE_ERROR_TYPES.has(result.error.type ?? '') ? 'declined' : 'unsettled';
  }
  // The intent exists and has not settled. Some of those states are definitive and some are not, and
  // the difference decides whether the buyer may retry under a fresh key.
  if (!status) {
    return 'unsettled';
  }
  return IN_FLIGHT_PAYMENT_STATUSES.has(status) ? 'unsettled' : 'declined';
}



/**
 * Whether a `declined` verdict lets the buyer try again with the SAME idempotency key.
 *
 * It does not, and that is the point of asking. Stripe replays a cached response — including a
 * decline — for the same key for 24 hours, so reusing it after a real decline means the buyer fixes
 * their card and gets the old refusal back. Only `unsettled` keeps its key, because there the first
 * attempt may have worked and a fresh key would risk charging twice.
 */
export function shouldMintFreshKey(verdict: CheckoutVerdict): boolean {
  return verdict === 'declined';
}

export interface PrepareAttempt {
  /** The Stripe action key currently bound to `fingerprint`. */
  key: string;
  /** Absent until this key has actually been sent to prepare. */
  fingerprint?: string;
}

/**
 * Bind one idempotency key to one exact prepare request.
 *
 * An exact retry must keep its key: that is what prevents a lost response from creating a second
 * Customer and charge. A request whose address, email, tax identity, coupon, or target changed is a
 * different Stripe request, however, and Stripe rejects different parameters under the old key.
 * The caller passes the SAME object it serializes into the request body so this cannot drift into a
 * hand-maintained list of fields.
 */
export function bindPrepareAttempt(
  current: PrepareAttempt,
  prepareInput: unknown,
  mintKey: () => string,
): PrepareAttempt {
  const fingerprint = JSON.stringify(prepareInput);
  return {
    key: current.fingerprint === undefined || current.fingerprint === fingerprint
      ? current.key
      : mintKey(),
    fingerprint,
  };
}

/**
 * Run the confirm step. Returns the verdict; never throws for a payment outcome.
 *
 * `redirect: 'if_required'` keeps the buyer on our page for every card that does not need a bank
 * challenge, which is the whole reason for building this screen. A card that DOES need one is sent
 * to `returnUrl` by Stripe and comes back through the page's own return handling.
 */
export async function confirmCheckout(args: {
  stripe: StripeInstance;
  elements: StripeElements;
  clientSecret: string;
  intentKind: CheckoutIntentKind;
  returnUrl: string;
}): Promise<CheckoutVerdict> {
  const { stripe, elements, clientSecret, intentKind, returnUrl } = args;
  if (intentKind === CheckoutIntentKind.CHECKOUT_INTENT_NONE) {
    // Nothing to confirm — Stripe already settled a zero invoice. The grant still arrives on the
    // webhook, so this is a success, not a no-op to report as a failure.
    return 'paid';
  }
  if (!clientSecret) {
    // A secret we were promised and did not get. Nothing has been charged, but we cannot prove it
    // from here, so it is not ours to call declined.
    return 'unsettled';
  }
  const confirm = intentKind === CheckoutIntentKind.CHECKOUT_INTENT_SETUP
    ? stripe.confirmSetup
    : stripe.confirmPayment;
  try {
    const result = await confirm.call(stripe, {
      elements,
      clientSecret,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    return verdictFromConfirm(intentKind, result);
  } catch {
    // The call threw, so the outcome is lost from THIS call — not from Stripe. The payment may have
    // gone through.
    return 'unsettled';
  }
}

/**
 * Whether the authoritative invoice changes anything in the quote the buyer can see.
 *
 * The screen quotes tax from PreviewCheckoutTax while the buyer types, then reads the authoritative
 * breakdown off the finalized invoice at confirm time. An inclusive price can keep the same total
 * while its tax rate, amount, label, or reverse-charge treatment changes. Those are still visible
 * purchase terms, and the service contract requires the finalized quote to replace the preview
 * before confirmation. The first comparison stops and paints it; comparing against that painted
 * quote on the next click makes the acknowledgement terminate rather than loop.
 */
export function visibleQuoteChanged(
  shown: CheckoutQuote | undefined,
  authoritative: CheckoutQuote | undefined,
): boolean {
  if (!shown || !authoritative) {
    return false;
  }
  return Number(shown.subtotalMinor ?? 0) !== Number(authoritative.subtotalMinor ?? 0)
    || Number(shown.discountMinor ?? 0) !== Number(authoritative.discountMinor ?? 0)
    || Number(shown.taxMinor ?? 0) !== Number(authoritative.taxMinor ?? 0)
    || Number(shown.totalMinor ?? 0) !== Number(authoritative.totalMinor ?? 0)
    || (shown.currency ?? '').toLowerCase() !== (authoritative.currency ?? '').toLowerCase()
    || Number(shown.taxRateBps ?? 0) !== Number(authoritative.taxRateBps ?? 0)
    || (shown.taxLabel ?? '') !== (authoritative.taxLabel ?? '')
    || Boolean(shown.taxInclusive) !== Boolean(authoritative.taxInclusive)
    || Boolean(shown.reverseCharge) !== Boolean(authoritative.reverseCharge)
    || Boolean(shown.taxDetermined) !== Boolean(authoritative.taxDetermined);
}

/**
 * Which Elements mode this purchase calls for. All three of Stripe's, because all three happen here.
 *
 * The mode is a contract with Stripe, not a label: it decides which payment methods are offered and
 * what mandate is taken. A subscription built in `payment` mode renders the one-time shape, can save
 * a method that is not eligible to bill again, and can fail at confirm against the invoice's own
 * PaymentIntent — so the three cases are pinned:
 *
 * - top-up, whatever it costs → `payment`. One charge, nothing to bill later.
 * - subscription with something due → `subscription`. A charge now and recurring billing after it.
 * - subscription with nothing due → `setup`. A free first period still needs a card for the renewal.
 */
export function elementsModeFor(
  quote: CheckoutQuote | undefined,
  isSubscription: boolean,
): ElementsMode {
  if (Number(quote?.totalMinor ?? 0) > 0) {
    return isSubscription ? 'subscription' : 'payment';
  }
  // Nothing due. Setup mode carries no amount, which is the point: `payment` mode needs one, and
  // there is no honest number to give it. See collectsCard for what is actually rendered.
  return 'setup';
}

/**
 * Whether this purchase needs a card at all.
 *
 * A one-time purchase that costs nothing does not — and asking anyway is not a harmless extra step.
 * The Payment Element mounts before the invoice exists, so a fully discounted top-up rendered a card
 * form, and a wallet or instalment option could advertise 0.01 because `payment` mode rejects a zero
 * amount and the screen was passing a floor of one minor unit. The buyer was being asked to pay a
 * cent for something free.
 *
 * A subscription always needs one, however little is due today, because it bills again later.
 */
export function collectsCard(quote: CheckoutQuote | undefined, isSubscription: boolean): boolean {
  return isSubscription || Number(quote?.totalMinor ?? 0) > 0;
}
